// Copyright (C) 2012-present, The Authors. This program is free software: you can redistribute it and/or  modify it under the terms of the GNU Affero General Public License, version 3, as published by the Free Software Foundation. This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU Affero General Public License for more details. You should have received a copy of the GNU Affero General Public License along with this program.  If not, see <http://www.gnu.org/licenses/>.

import { Heading, Box, Text } from 'theme-ui'
import { useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useAuth } from 'react-oidc-context'
import emoji from 'react-easy-emoji'

import { CheckboxField } from './CheckboxField'
import strings from '../../strings/strings'
import { useConversationData } from '../../util/conversation_data'
import { hasDelphiEnabled } from '../../util/auth'
import ModerateCommentsSeed from './ModerateCommentSeed'
import Spinner from '../framework/Spinner'
import {
  handleConversationDataUpdate,
  optimisticConversationDataUpdateOnTyping
} from '../../actions'

const ConversationConfig = () => {
  const dispatch = useDispatch()
  const conversationData = useConversationData()
  const { loading, error } = conversationData
  const { user: authUser } = useAuth()
  const topicRef = useRef(null)
  const descriptionRef = useRef(null)

  const handleStringValueChange = useCallback(
    (field, value) => {
      let val = value
      if (field === 'help_bgcolor' || field === 'help_color') {
        if (!val.length) {
          val = 'default'
        }
      }
      dispatch(handleConversationDataUpdate(conversationData, field, val))
    },
    [dispatch, conversationData]
  )

  const handleConfigInputTyping = useCallback(
    (field, value) => {
      dispatch(optimisticConversationDataUpdateOnTyping(conversationData, field, value))
    },
    [dispatch, conversationData]
  )

  if (loading && !topicRef.current && !descriptionRef.current) {
    return <Spinner />
  }

  return (
    <Box>
      <Heading
        as="h3"
        sx={{
          fontSize: [3, null, 4],
          lineHeight: 'body',
          mb: [3, null, 4]
        }}>
        {strings('cfg_configure')}
      </Heading>
      <Box sx={{ mb: [4] }}>
        {loading ? (
          <Text>{emoji('💾')} {strings('cfg_saving')}</Text>
        ) : (
          <Text>{emoji('⚡')} {strings('cfg_uptodate')}</Text>
        )}
        {error ? <Text>{strings('cfg_error_saving')}</Text> : null}
      </Box>

      <CheckboxField field="is_active" label={strings('cfg2_conversation_is_open')}>
        {strings('cfg_is_active_desc')}
      </CheckboxField>

      <Box sx={{ mb: [3] }}>
        <Text sx={{ display: 'block', mb: [2] }}>{strings('cfg_topic')}</Text>
        <input
          ref={topicRef}
          sx={{
            display: 'block',
            fontFamily: 'body',
            fontSize: [2],
            width: ['100%', '100%', '35em'],
            maxWidth: ['100%', '100%', '35em'],
            borderRadius: 2,
            padding: [2],
            border: '1px solid',
            borderColor: 'mediumGray'
          }}
          data-testid="topic"
          onBlur={(e) => handleStringValueChange('topic', e.target.value)}
          onChange={(e) => handleConfigInputTyping('topic', e.target.value)}
          value={conversationData.topic || ''}
        />
      </Box>

      <Box sx={{ mb: [3] }}>
        <Text sx={{ display: 'block', mb: [2] }}>{strings('cfg_description')}</Text>
        <textarea
          ref={descriptionRef}
          sx={{
            display: 'block',
            fontFamily: 'body',
            fontSize: [2],
            width: ['100%', '100%', '35em'],
            maxWidth: ['100%', '100%', '35em'],
            height: '7em',
            resize: 'none',
            padding: [2],
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'mediumGray'
          }}
          data-testid="description"
          onBlur={(e) => handleStringValueChange('description', e.target.value)}
          onChange={(e) => handleConfigInputTyping('description', e.target.value)}
          value={conversationData.description || ''}
        />
      </Box>

      <Heading
        as="h6"
        sx={{
          fontSize: [1, null, 2],
          lineHeight: 'body',
          my: [3, null, 4]
        }}>
        {strings('cfg_seed_comments')}
      </Heading>
      <ModerateCommentsSeed params={{ conversation_id: conversationData.conversation_id }} />

      <Heading
        as="h6"
        sx={{
          fontSize: [1, null, 2],
          lineHeight: 'body',
          my: [3, null, 4]
        }}>
        {strings('cfg_customize_ui')}
      </Heading>

      <CheckboxField field="vis_type" label={strings('cfg2_visualization')} isIntegerBool>
        {strings('cfg_vis_desc')}
      </CheckboxField>

      <CheckboxField field="write_type" label={strings('cfg2_comment_form')} isIntegerBool>
        {strings('cfg_write_desc')}
      </CheckboxField>

      <CheckboxField field="help_type" label={strings('cfg2_help_text')} isIntegerBool>
        {strings('cfg_help_desc')}
      </CheckboxField>

      <CheckboxField
        field="subscribe_type"
        label={strings('cfg2_subscribe_prompt')}
        isIntegerBool>
        {strings('cfg_subscribe_desc')}
      </CheckboxField>

      <CheckboxField field="strict_moderation">
        {strings('cfg_strict_mod_desc')}
      </CheckboxField>

      <CheckboxField field="treevite_enabled" label={strings('cfg2_enable_invite_tree')}>
        {strings('cfg_treevite_desc')}
      </CheckboxField>

      <CheckboxField field="importance_enabled" label={strings('cfg2_importance_enabled')}>
        {strings('cfg_importance_desc')}
      </CheckboxField>

      <CheckboxField field="demographics_enabled" label={strings('cfg2_demographics_survey')}>
        {strings('cfg_demographics_desc')}
      </CheckboxField>

      {hasDelphiEnabled(authUser) && (
        <CheckboxField field="topics_enabled" label={strings('cfg2_topics_enabled')}>
          {strings('cfg_topics_desc')}
        </CheckboxField>
      )}
    </Box>
  )
}

export default ConversationConfig
